import { useEffect } from 'react';
import { useStateInRef } from 'seagull/utils/useStateInRef';
import { BOOT_UNREACHABLE_REPORT_MS } from '~/boot-timeouts';
import { useIoContext, useStore, useStoreStatic } from '~/contexts';
import { useNetworkRetry } from '~/hooks/networkRetry/useNetworkRetry';
import { useSetAuthToken } from '~/hooks/useSetAuthToken';
import { selectors } from '~/store/state-selectors';
export class BootUnreachableError extends Error {
    constructor(){
        super('Boot unreachable: the network failure screen is still showing after the report delay');
    }
}
export function useBootNetworkRetry(schedule) {
    const bootFailedOnNetwork = useStore(selectors.isBootNetworkFailure);
    const setAuthToken = useSetAuthToken();
    const { getState } = useStoreStatic();
    const { cookies, browser, getNow } = useIoContext();
    const networkRetry = useNetworkRetry({
        active: bootFailedOnNetwork,
        attempt: ()=>setAuthToken(cookies.getAuthToken()),
        schedule
    });
    const getRetryThenReportIfStillUnreachable = useStateInRef(retryThenReportIfStillUnreachable);
    useEffect(()=>{
        if (bootFailedOnNetwork && !browser.isAutomated()) {
            const shownAtMs = getNow().getTime();
            return browser.schedule(BOOT_UNREACHABLE_REPORT_MS, ()=>{
                // WHY: link-scanner sandboxes fast-forward timers, so the timer firing does not prove the delay passed.
                if (getNow().getTime() - shownAtMs >= BOOT_UNREACHABLE_REPORT_MS) {
                    void getRetryThenReportIfStillUnreachable()();
                }
            });
        }
    }, [
        bootFailedOnNetwork
    ]);
    // WHY: a slept device wakes past the delay, so only an attempt settling after it — and only while the device is online — proves Anchor itself is unreachable.
    async function retryThenReportIfStillUnreachable() {
        await networkRetry.retryNow('scheduled');
        if (selectors.isBootNetworkFailure(getState()) && browser.readPageLoadStatus().online) {
            browser.reportError(new BootUnreachableError());
        }
    }
    return networkRetry;
}
